import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ACCENT_BG, type AccentToken } from '@/shared/utils/accentTokens';
import { dashboardPanelSx } from '@/shared/styles/surfaces';

interface StatCardProps {
  label: string;
  value: number | string;
  icon?: ReactNode;
  subtitle?: string;
  accent?: AccentToken;
  onClick?: () => void;
}

export function StatCard({
  label,
  value,
  icon,
  subtitle,
  accent = 'primary.main',
  onClick,
}: StatCardProps) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...dashboardPanelSx,
        position: 'relative',
        p: 2.5,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: accent,
          opacity: 0.85,
        },
        '&:hover': onClick
          ? {
              boxShadow: 'var(--shadow-md)',
              transform: 'translateY(-3px)',
              borderColor: 'primary.100',
            }
          : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'textColors.muted',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.65rem',
              lineHeight: 1.4,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              mt: 0.5,
              fontWeight: 800,
              letterSpacing: '-0.04em',
              lineHeight: 1,
              fontSize: { xs: '1.75rem', sm: '2rem' },
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              bgcolor: ACCENT_BG[accent],
              color: accent,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </Paper>
  );
}
